import React, { useState } from 'react';
import ToolLayout from '../components/ToolLayout';

const Base64Tool: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');

  const encodeBase64 = () => {
    setError('');
    setSuccess('');
    
    if (!input.trim()) {
      setError('Vui lòng nhập text để encode');
      return;
    }

    try {
      const encoded = btoa(unescape(encodeURIComponent(input)));
      setOutput(encoded);
      setSuccess('Text đã được encode thành Base64 thành công!');
    } catch (err) {
      setError('Không thể encode: ' + (err as Error).message);
      setOutput('');
    }
  };

  const decodeBase64 = () => {
    setError('');
    setSuccess('');
    
    if (!input.trim()) {
      setError('Vui lòng nhập Base64 string để decode');
      return;
    }

    try {
      const decoded = decodeURIComponent(escape(atob(input)));
      setOutput(decoded);
      setSuccess('Base64 đã được decode thành công!');
    } catch (err) {
      setError('Base64 string không hợp lệ: ' + (err as Error).message);
      setOutput('');
    }
  };

  const handleProcess = () => {
    if (mode === 'encode') {
      encodeBase64();
    } else {
      decodeBase64();
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    setSuccess('');
  };

  const copyToClipboard = async () => {
    if (output) {
      try {
        await navigator.clipboard.writeText(output);
        setSuccess('Đã copy vào clipboard!');
      } catch (err) {
        setError('Không thể copy vào clipboard');
      }
    }
  };

  const swapInputOutput = () => {
    if (output) {
      setInput(output);
      setOutput('');
      setMode(mode === 'encode' ? 'decode' : 'encode');
      setError('');
      setSuccess('');
    }
  };

  return (
    <ToolLayout 
      title="Base64 Encoder/Decoder" 
      description="Encode và decode text sử dụng Base64 encoding"
    >
      {/* Mode Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              value="encode"
              checked={mode === 'encode'}
              onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            />
            <span>Encode (Text → Base64)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              value="decode"
              checked={mode === 'decode'}
              onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            />
            <span>Decode (Base64 → Text)</span>
          </label>
        </div>
      </div>

      <div className="input-group">
        <label className="input-label">
          {mode === 'encode' ? 'Nhập text để encode:' : 'Nhập Base64 string để decode:'}
        </label>
        <textarea
          className="input-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={mode === 'encode' 
            ? 'Nhập text của bạn ở đây...\n\nVí dụ: Hello World!' 
            : 'Nhập Base64 string ở đây...\n\nVí dụ: SGVsbG8gV29ybGQh'
          }
        />
      </div>

      <div className="button-group">
        <button className="action-button" onClick={handleProcess}>
          {mode === 'encode' ? 'Encode to Base64' : 'Decode from Base64'}
        </button>
        {output && (
          <button className="action-button" onClick={swapInputOutput}>
            ⇅ Swap & Switch Mode
          </button>
        )}
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {output && (
        <div className="input-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label className="input-label">
              {mode === 'encode' ? 'Base64 Result:' : 'Decoded Text:'}
            </label>
            <button className="action-button" onClick={copyToClipboard}>
              Copy
            </button>
          </div>
          <textarea
            className="input-textarea"
            value={output}
            readOnly
          />
        </div>
      )}

      {/* Info Section */}
      <div className="tool-info-section">
        <h4 className="tool-info-title">ℹ️ Về Base64:</h4>
        <ul className="tool-info-list">
          <li>
            Base64 là một phương thức mã hóa dữ liệu binary thành text ASCII
          </li>
          <li>
            Thường được sử dụng để truyền tải dữ liệu qua các giao thức chỉ hỗ trợ text
          </li>
          <li>
            Sử dụng 64 ký tự: A-Z, a-z, 0-9, +, / (và = để padding)
          </li>
          <li>
            Kích thước sau khi encode sẽ tăng khoảng 33% so với dữ liệu gốc
          </li>
        </ul>
      </div>

      <style>{`
        .tool-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--border-color);
        }

        .tool-info-title {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-weight: 600;
        }

        .tool-info-list {
          margin: 0;
          padding-left: 1.5rem;
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .tool-info-list li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </ToolLayout>
  );
};

export default Base64Tool;

